/**
 * Shared opening-hours engine (client-safe, no imports).
 *
 * Resolution order for "is this venue open right now?":
 *   1. temporarily_closed  → closed
 *   2. special_hours entry for today (exceptional hours) → wins over weekly
 *   3. regular weekly opening_hours
 *
 * All calculations happen in Europe/Helsinki regardless of the viewer's clock,
 * so a visitor in another timezone still sees Finnish opening status.
 */

export type DayHours = {
  open?: string | null;
  close?: string | null;
  closed?: boolean | null;
};

/** `{ Mon: { open: "11:00", close: "22:00" }, Tue: { closed: true }, … }` */
export type WeeklyHours = Record<string, DayHours> | null | undefined;

export type SpecialHour = {
  /** ISO date, `YYYY-MM-DD` */
  date: string;
  closed?: boolean;
  open?: string | null;
  close?: string | null;
  note?: string | null;
};

export type HoursSource = {
  /** Free-text schedule ("Mon–Sat: 12:00–23:00"). Used only when structured hours are missing. */
  schedule_info?: string | null;
  opening_hours?: WeeklyHours;
  kitchen_hours?: WeeklyHours;
  lunch_hours?: WeeklyHours;
  special_hours?: SpecialHour[] | null;
  temporarily_closed?: boolean | null;
  temporarily_closed_note?: string | null;
};

export type OpenState = "open" | "closing_soon" | "closed" | "unknown";

export type OpenStatus = {
  state: OpenState;
  /** Short human label: "Open now", "Closing soon", "Opens at 11:00", "Closed" */
  label: string;
  /** Today's hours as a display string: "11:00 – 22:00" or "Closed" */
  todayLabel: string;
  /** Present when the venue is currently closed but opens later today/tomorrow */
  opensAt?: string;
  /** Present when open — the time it closes */
  closesAt?: string;
  /** True when today's hours come from an exceptional-hours entry */
  exceptional: boolean;
  note?: string | null;
};

export const DAY_KEYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;
export type DayKey = (typeof DAY_KEYS)[number];

export const DAY_LABELS: Record<DayKey, string> = {
  Mon: "Monday",
  Tue: "Tuesday",
  Wed: "Wednesday",
  Thu: "Thursday",
  Fri: "Friday",
  Sat: "Saturday",
  Sun: "Sunday",
};

const TZ = "Europe/Helsinki";

/** Current wall-clock in Helsinki: `{ dayKey, minutes, isoDate }` */
export function helsinkiNow(now: Date = new Date()): {
  dayKey: DayKey;
  minutes: number;
  isoDate: string;
} {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: TZ,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour12: false,
  }).formatToParts(now);

  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  const weekday = get("weekday").slice(0, 3);
  const dayKey = (DAY_KEYS.find((d) => d.toLowerCase() === weekday.toLowerCase()) ?? "Mon") as DayKey;
  const hour = Number(get("hour"));
  const minute = Number(get("minute"));
  const isoDate = `${get("year")}-${get("month")}-${get("day")}`;

  return {
    dayKey,
    // Intl can return "24" for midnight in some engines
    minutes: (hour % 24) * 60 + minute,
    isoDate,
  };
}

export function toMinutes(hhmm?: string | null): number | null {
  if (!hhmm) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (Number.isNaN(h) || Number.isNaN(min)) return null;
  return h * 60 + min;
}

/** Reads a day from a weekly-hours object, tolerating casing variations. */
export function dayOf(hours: WeeklyHours, key: DayKey): DayHours | null {
  if (!hours || typeof hours !== "object") return null;
  const direct = hours[key];
  if (direct) return direct;
  const lower = hours[key.toLowerCase()];
  if (lower) return lower;
  const full = hours[DAY_LABELS[key]] ?? hours[DAY_LABELS[key].toLowerCase()];
  return full ?? null;
}

export function formatDayHours(day: DayHours | null): string {
  if (!day) return "—";
  if (day.closed) return "Closed";
  if (!day.open || !day.close) return "—";
  return `${day.open} – ${day.close}`;
}

function nextOpenDay(
  hours: WeeklyHours,
  fromDay: DayKey,
): { dayKey: DayKey; open: string } | null {
  const startIdx = DAY_KEYS.indexOf(fromDay);
  for (let i = 1; i <= 7; i++) {
    const key = DAY_KEYS[(startIdx + i) % 7] as DayKey;
    const d = dayOf(hours, key);
    if (d && !d.closed && d.open) return { dayKey: key, open: d.open };
  }
  return null;
}

const DAY_ALIASES: Record<string, DayKey> = {
  mon: "Mon", monday: "Mon", ma: "Mon", maanantai: "Mon",
  tue: "Tue", tues: "Tue", tuesday: "Tue", ti: "Tue", tiistai: "Tue",
  wed: "Wed", weds: "Wed", wednesday: "Wed", ke: "Wed", keskiviikko: "Wed",
  thu: "Thu", thur: "Thu", thurs: "Thu", thursday: "Thu", to: "Thu", torstai: "Thu",
  fri: "Fri", friday: "Fri", pe: "Fri", perjantai: "Fri",
  sat: "Sat", saturday: "Sat", la: "Sat", lauantai: "Sat",
  sun: "Sun", sunday: "Sun", su: "Sun", sunnuntai: "Sun",
};

function normTime(h: string, m?: string): string {
  return `${String(Number(h)).padStart(2, "0")}:${m ?? "00"}`;
}

/**
 * Best-effort parser for free-text schedules such as:
 *   "Mon–Sat: 12:00–23:00\nSun: 16:30–23:00"
 * Only the opening-hours block is used; "Lunch"/"Kitchen" blocks are ignored.
 */
export function parseScheduleText(text?: string | null): WeeklyHours {
  if (!text || typeof text !== "string") return null;
  const week: Record<string, DayHours> = {};
  let stop = false;
  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;
    const lower = line.toLowerCase();
    const hasTime = /\d{1,2}(?:[:.]\d{2})?/.test(line);
    if (!hasTime && /(lunch|kitchen|lounas|keittiö|brunch|happy hour)/i.test(lower)) { stop = true; continue; }
    if (!hasTime && /(opening|hours|aukiolo)/i.test(lower)) { stop = false; continue; }
    if (stop) continue;

    const dayMatch = /^([a-zäöå]{2,10})\s*(?:[-–—]\s*([a-zäöå]{2,10}))?\s*[:.]?\s*(.*)$/i.exec(line);
    if (!dayMatch) continue;
    const from = DAY_ALIASES[dayMatch[1].toLowerCase()];
    if (!from) continue;
    const to = dayMatch[2] ? DAY_ALIASES[dayMatch[2].toLowerCase()] : from;
    if (!to) continue;
    const rest = dayMatch[3] ?? "";

    let entry: DayHours | null = null;
    if (/closed|suljettu/i.test(rest)) entry = { closed: true };
    else {
      const t = /(\d{1,2})(?:[:.](\d{2}))?\s*[-–—to]{1,2}\s*(\d{1,2})(?:[:.](\d{2}))?/i.exec(rest);
      if (t) entry = { open: normTime(t[1], t[2]), close: normTime(t[3], t[4]), closed: false };
    }
    if (!entry) continue;

    const startIdx = DAY_KEYS.indexOf(from);
    const endIdx = DAY_KEYS.indexOf(to);
    const span = (endIdx - startIdx + 7) % 7;
    for (let i = 0; i <= span; i++) {
      const key = DAY_KEYS[(startIdx + i) % 7];
      if (!week[key]) week[key] = entry;
    }
  }
  return Object.keys(week).length ? week : null;
}

/**
 * Resolves the effective hours for today and turns them into a display status.
 *
 * `closingSoonMinutes` defaults to 60 — the window in which we show
 * "Closing soon" instead of "Open now".
 */
export function computeOpenStatus(
  source: HoursSource,
  opts: { now?: Date; closingSoonMinutes?: number; weekly?: WeeklyHours } = {},
): OpenStatus {
  const closingSoon = opts.closingSoonMinutes ?? 60;
  const { dayKey, minutes, isoDate } = helsinkiNow(opts.now);
  const structured = opts.weekly ?? source.opening_hours;
  const hasStructured =
    !!structured && typeof structured === "object" && Object.keys(structured).length > 0;
  const weekly = hasStructured ? structured : parseScheduleText(source.schedule_info);

  if (source.temporarily_closed) {
    return {
      state: "closed",
      label: "Temporarily closed",
      todayLabel: "Temporarily closed",
      exceptional: true,
      note: source.temporarily_closed_note ?? null,
    };
  }

  const special = (source.special_hours ?? []).find((s) => s && s.date === isoDate);
  const today: DayHours | null = special
    ? { open: special.open, close: special.close, closed: special.closed }
    : dayOf(weekly, dayKey);

  const todayLabel = formatDayHours(today);

  if (!today || (!today.closed && (!today.open || !today.close))) {
    return { state: "unknown", label: "Hours not available", todayLabel, exceptional: !!special };
  }

  if (today.closed) {
    const next = nextOpenDay(weekly, dayKey);
    return {
      state: "closed",
      label: next ? `Closed · opens ${DAY_LABELS[next.dayKey].slice(0, 3)} ${next.open}` : "Closed today",
      todayLabel: "Closed",
      exceptional: !!special,
      note: special?.note ?? null,
    };
  }

  const openM = toMinutes(today.open);
  let closeM = toMinutes(today.close);
  if (openM === null || closeM === null) {
    return { state: "unknown", label: "Hours not available", todayLabel, exceptional: !!special };
  }
  // Past-midnight closing ("18:00 – 02:00")
  const overnight = closeM <= openM;
  if (overnight) closeM += 24 * 60;
  const nowM = minutes < openM && overnight && minutes + 24 * 60 < closeM ? minutes + 24 * 60 : minutes;

  if (nowM >= openM && nowM < closeM) {
    const left = closeM - nowM;
    return {
      state: left <= closingSoon ? "closing_soon" : "open",
      label: left <= closingSoon ? `Closing soon · ${today.close}` : `Open now · until ${today.close}`,
      todayLabel,
      closesAt: today.close ?? undefined,
      exceptional: !!special,
      note: special?.note ?? null,
    };
  }

  if (nowM < openM) {
    return {
      state: "closed",
      label: `Opens at ${today.open}`,
      todayLabel,
      opensAt: today.open ?? undefined,
      exceptional: !!special,
      note: special?.note ?? null,
    };
  }

  const next = nextOpenDay(weekly, dayKey);
  return {
    state: "closed",
    label: next ? `Closed · opens ${DAY_LABELS[next.dayKey].slice(0, 3)} ${next.open}` : "Closed",
    todayLabel,
    exceptional: !!special,
    note: special?.note ?? null,
  };
}

/** Kitchen / lunch status reuse the same engine with a different weekly set. */
export function computeServiceStatus(source: HoursSource, which: "kitchen" | "lunch", now?: Date) {
  const weekly = which === "kitchen" ? source.kitchen_hours : source.lunch_hours;
  if (!weekly) return null;
  return computeOpenStatus(source, { now, weekly });
}

/** True when the venue serves lunch today (used by the hub's Lunch filter). */
export function servesToday(hours: WeeklyHours, now?: Date): boolean {
  const { dayKey } = helsinkiNow(now);
  const d = dayOf(hours, dayKey);
  return !!d && !d.closed && !!d.open;
}

export function isStale(iso: string | null | undefined, days = 30): boolean {
  if (!iso) return true;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return true;
  return Date.now() - then > days * 24 * 60 * 60 * 1000;
}

export function emptyWeek(): Record<DayKey, DayHours> {
  return DAY_KEYS.reduce(
    (acc, d) => ({ ...acc, [d]: { open: "", close: "", closed: false } }),
    {} as Record<DayKey, DayHours>,
  );
}
